import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AlertTriangle,
  ClipboardCheck,
  FileText,
  Info,
  Plus,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ListItemCardCst from "@components/ListItemCardCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import SelectedLOList from "@components/SelectedLOList";
import AddTestSurveyModalCst, {
  AddTestSurveySelectedItem,
} from "@components/AddTestSurveyModalCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { AssociationItem } from "../../AddActivity/types";
import { TestLogic } from "../../Course/types";
import { TEST_LOGIC_OPTIONS } from "../config/pathOptions";
import { PathWizardData } from "../types";

const SectionDivider: React.FC = () => <Divider sx={{ opacity: 0.6 }} />;

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: "18px",
        fontWeight: 700,
        color: theme.palette.text.primary,
      }}
    >
      {title}
    </Typography>
  );
};

interface TestsSurveyStepProps extends WizardStepProps<PathWizardData> {
  lockTestSurvey?: boolean;
}

const TestsSurveyStep: React.FC<TestsSurveyStepProps> = ({
  data,
  setData,
  errors,
  lockTestSurvey = false,
}) => {
  const { t } = useTranslation("wizard-path");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [pickerOpen, setPickerOpen] = useState(false);

  const items = data.selected_los;
  const noItems = items.length === 0;
  const hasTest = !!data.test_id;
  const hasSurvey = !!data.survey_id;
  const showLogicSection = hasTest;
  const showMaxAttempt = data.test_logic === "WITH_ZEROING";

  const errMsg = (key: string): string =>
    errors[key] ? t(`errors.${errors[key]}`) || "" : "";

  const handlePickerConfirm = (selection: AddTestSurveySelectedItem | null) => {
    if (!selection) return;
    if (selection.kind === "test") {
      setData({ test_id: { id: selection.id, label: selection.label } });
    } else {
      setData({ survey_id: { id: selection.id, label: selection.label } });
    }
  };

  const handleRemoveTest = () => {
    setData({
      test_id: null,
      test_logic: null,
      test_min_score: "",
      max_attempt: "",
    });
  };

  const handleRemoveSurvey = () => {
    setData({ survey_id: null });
  };

  const handleRemoveChild = (id: number) => {
    setData({ selected_los: items.filter((it) => it.id !== id) });
  };

  // TODO: pagina dettaglio v2 non disponibile.
  const handleDetailsTest = () => {
    console.info("[path-wizard] test details — TODO", data.test_id?.id);
  };
  const handleDetailsSurvey = () => {
    console.info("[path-wizard] survey details — TODO", data.survey_id?.id);
  };

  // TODO: integrare creazione nuovo test/survey.
  const handleCreateNew = (tabKey?: string) => {
    console.info("[path-wizard] crea nuovo test o survey — TODO", tabKey);
  };

  const renderAssociationRow = (
    item: AssociationItem,
    kind: "test" | "survey",
    onDetails: () => void,
    onRemove: () => void,
  ) => (
    <ListItemCardCst
      icon={
        kind === "test" ? <ClipboardCheck size={20} /> : <FileText size={20} />
      }
      title={item.label || `#${item.id}`}
      subtitle={{
        id: item.id,
        activityType: kind === "test" ? "TEST" : "SURVEY",
      }}
      actions={[
        // {
        //   label: t("step-tests-survey.details") || "Dettagli",
        //   onClick: onDetails,
        // },
        {
          label: t("step-tests-survey.remove") || "Rimuovi",
          icon: <Trash2 size={18} />,
          iconOnly: true,
          disabled: lockTestSurvey,
          onClick: () => {
            if (lockTestSurvey) return;
            onRemove();
          },
        },
      ]}
    />
  );

  const childrenCount = items.length;
  const activitiesCount = items.filter((it) => it.kind === "activity").length;
  const coursesCount = items.filter((it) => it.kind === "course").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {noItems && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} color={theme.palette.warning.dark} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-content.empty-warning")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          padding: "12px 16px",
          borderRadius: "12px",
          backgroundColor: theme.palette.error.light,
          border: `1px solid ${theme.palette.error.main}`,
        }}
      >
        <Info size={20} color={theme.palette.error.dark} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: theme.palette.common.black,
          }}
        >
          {t("step-tests-survey.banner")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => setPickerOpen(true)}
          variant="contained"
          startIcon={<Plus size={16} />}
          disabled={(hasTest && hasSurvey) || lockTestSurvey}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("step-tests-survey.add")}
        </Button>
        {/*
        <Button
          onClick={() => handleCreateNew()}
          variant="outlined"
          startIcon={<Pencil size={16} />}
          disabled={lockTestSurvey}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("step-tests-survey.create-new")}
        </Button>
        */}
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: theme.palette.text.tertiary,
          }}
        >
          {[
            childrenCount > 0 &&
              t("step-tests-survey.counter.content", { count: childrenCount }),
            activitiesCount > 0 &&
              t("step-tests-survey.counter.activities", {
                count: activitiesCount,
              }),
            coursesCount > 0 &&
              t("step-tests-survey.counter.courses", { count: coursesCount }),
            hasTest && t("step-tests-survey.counter.test", { count: 1 }),
            hasSurvey && t("step-tests-survey.counter.survey", { count: 1 }),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <SectionHeader title={t("step-tests-survey.sections.content")} />
        {noItems ? (
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
          >
            {t("step-tests-survey.content-empty")}
          </Typography>
        ) : (
          <SelectedLOList
            items={items}
            tCatalog={tCatalog}
            removeLabel={t("step-tests-survey.remove")}
            onRemove={handleRemoveChild}
            onDetails={(it) =>
              console.info(
                "[path-wizard] content details — TODO",
                it.kind,
                it.id,
              )
            }
          />
        )}
      </Box>

      {(hasTest || hasSurvey) && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <SectionHeader
              title={t("step-tests-survey.sections.tests-survey")}
            />
            {hasTest &&
              data.test_id &&
              renderAssociationRow(
                data.test_id,
                "test",
                handleDetailsTest,
                handleRemoveTest,
              )}
            {hasSurvey &&
              data.survey_id &&
              renderAssociationRow(
                data.survey_id,
                "survey",
                handleDetailsSurvey,
                handleRemoveSurvey,
              )}
          </Box>
        </>
      )}

      {showLogicSection && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {t("step-tests-survey.logic.title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.tertiary,
                  lineHeight: 1.5,
                  mt: 0.5,
                }}
              >
                {t("step-tests-survey.logic.description")}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SelectCst
                  id="path-test-logic"
                  label={`${t("step-tests-survey.logic.field-logic")} *`}
                  value={data.test_logic || ""}
                  onChange={(v: any) => {
                    const next = (v || null) as TestLogic | null;
                    setData({
                      test_logic: next,
                      max_attempt:
                        next === "WITH_ZEROING" ? data.max_attempt : "",
                    });
                  }}
                  options={TEST_LOGIC_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.i18nKey) || o.value,
                  }))}
                  error={!!errors.test_logic}
                  errorMessage={errMsg("test_logic")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputCst
                  id="path-test-min-score"
                  label={t("step-tests-survey.logic.field-min-score") || ""}
                  value={data.test_min_score}
                  onChange={(e: any) =>
                    setData({ test_min_score: e.target.value })
                  }
                  type="number"
                  min={0}
                  disabled={lockTestSurvey}
                />
              </Grid>
              {showMaxAttempt && (
                <Grid item xs={12} md={6}>
                  <InputCst
                    id="path-max-attempt"
                    label={`${t("step-tests-survey.logic.field-max-attempt")} *`}
                    value={data.max_attempt}
                    onChange={(e: any) =>
                      setData({ max_attempt: e.target.value })
                    }
                    type="number"
                    min={1}
                    error={!!errors.max_attempt}
                    errorMessage={errMsg("max_attempt")}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}

      <AddTestSurveyModalCst
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        onCreate={(tabKey) => handleCreateNew(tabKey)}
        initialTab={hasTest ? "survey" : "test"}
      />
    </Box>
  );
};

export default TestsSurveyStep;
